import { useMemo } from 'react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { CartesianGrid, Legend, Line, LineChart, Tooltip, XAxis, YAxis } from 'recharts';
import { TrendingUp } from 'lucide-react';

import { Card, CardContent, CardHeader, CardTitle } from '@/ui/base/card';
import { ChartContainer, type ChartConfig } from '@/ui/base/chart';
import { formatarDinheiro } from '@/nucleo/dinheiro';

import { PROPS_TOOLTIP, RISCA_GRADE, ROTULO_EIXO } from './temaGrafico';
import type { PontoEvolucaoCompetencia } from './tipos';

const CONFIG: ChartConfig = {
  fechado: { label: 'Fechado', color: 'hsl(var(--primary))' },
  distribuido: { label: 'Distribuído', color: 'hsl(var(--accent))' },
  pago: { label: 'Pago', color: 'hsl(var(--success))' },
};

/** Gráfico de linha: valores fechados, distribuídos e pagos por competência. */
export function EvolucaoPorCompetencia({ dados }: { dados: PontoEvolucaoCompetencia[] }) {
  const serie = useMemo(
    () =>
      dados.map((p) => ({
        mes: format(new Date(`${p.mes}T00:00:00`), 'MMM/yy', { locale: ptBR }),
        fechado: p.fechado,
        distribuido: p.distribuido,
        pago: p.pago,
      })),
    [dados],
  );

  return (
    <Card className="overflow-hidden rounded-2xl border border-border/60">
      <CardHeader className="pb-3">
        <div className="flex items-center gap-2.5">
          <div className="rounded-lg bg-orange/10 p-1.5">
            <TrendingUp className="h-4 w-4 text-orange" aria-hidden />
          </div>
          <CardTitle className="text-sm font-bold">Evolução por competência</CardTitle>
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        {serie.length === 0 ? (
          <p className="py-12 text-center text-sm text-muted-foreground">Sem dados no período.</p>
        ) : (
          <div className="w-full overflow-x-auto">
            <div className="min-w-[480px]">
              <ChartContainer config={CONFIG} className="aspect-auto h-72 w-full">
                <LineChart data={serie} margin={{ top: 10, right: 16, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={RISCA_GRADE} />
                  <XAxis dataKey="mes" tick={ROTULO_EIXO} />
                  <YAxis
                    tick={ROTULO_EIXO}
                    tickFormatter={(v: number) => formatarDinheiro(v).replace('R$', '')}
                  />
                  <Tooltip {...PROPS_TOOLTIP} formatter={(v: number) => formatarDinheiro(v)} />
                  <Legend />
                  <Line
                    type="monotone"
                    dataKey="fechado"
                    name="Fechado"
                    stroke="var(--color-fechado)"
                    strokeWidth={2}
                  />
                  <Line
                    type="monotone"
                    dataKey="distribuido"
                    name="Distribuído"
                    stroke="var(--color-distribuido)"
                    strokeWidth={2}
                  />
                  <Line
                    type="monotone"
                    dataKey="pago"
                    name="Pago"
                    stroke="var(--color-pago)"
                    strokeWidth={2}
                  />
                </LineChart>
              </ChartContainer>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
